const QSTOMIZER_INSTRUCTIONS_PATH = '/apps/qstomizer';
const QSTOMIZER_INSTRUCTIONS_TITLE_ID = 'qstomizer-instructions-title';

if (!customElements.get('qstomizer-instructions')) {
    customElements.define('qstomizer-instructions', class QstomizerInstructions extends HTMLElement {
        constructor() {
            super();
            // What had focus before the modal opened, so we can restore it on close.
            this.lastFocused = null;
            this.onClick = this.onClick.bind(this);
            this.onKeydown = this.onKeydown.bind(this);
        }

        connectedCallback() {
            // Only active on the Qstomizer app page. On any other page the element is inert
            // and renders nothing.
            if (window.location.pathname !== QSTOMIZER_INSTRUCTIONS_PATH) return;

            this.render();
            document.addEventListener('click', this.onClick);
            document.addEventListener('keydown', this.onKeydown);

            // Shown on load — go through open() so focus, inert, and ARIA state are all set.
            this.open();
        }

        disconnectedCallback() {
            document.removeEventListener('click', this.onClick);
            document.removeEventListener('keydown', this.onKeydown);
            if (!this.modalEl) return;
            // Undo global side effects, then remove the injected nodes.
            this.setBackgroundInert(false);
            document.body.classList.remove('qstomizer-instructions-open');
            this.triggerEl.remove();
            this.modalEl.remove();
        }

        // ---- Markup --------------------------------------------------------
        // Trigger and modal are appended to <body> (not inside this element) so their
        // position: fixed layout is measured against the viewport with no risk of a
        // transformed ancestor becoming their containing block.
        render() {
            this.triggerEl = document.createElement('button');
            this.triggerEl.type = 'button';
            this.triggerEl.classList.add('qstomizer-instructions--trigger', 'qstomizer-instructions-open');
            this.triggerEl.setAttribute('aria-haspopup', 'dialog');
            this.triggerEl.setAttribute('aria-expanded', 'true'); // modal is open on load
            this.triggerEl.innerHTML = `<span>View Guidelines</span>`;
            document.body.append(this.triggerEl);

            this.modalEl = document.createElement('div');
            this.modalEl.classList.add('qstomizer-instructions--modal-outer');
            this.modalEl.setAttribute('role', 'dialog');
            this.modalEl.setAttribute('aria-modal', 'true');
            this.modalEl.setAttribute('aria-labelledby', QSTOMIZER_INSTRUCTIONS_TITLE_ID);
            this.modalEl.setAttribute('tabindex', '-1'); // programmatically focusable on open
            this.modalEl.innerHTML = `
                <div class="qstomizer-instructions--modal-inner">
                    <div class="qstomizer-instructions">
                        <h2 id="${QSTOMIZER_INSTRUCTIONS_TITLE_ID}">Customizer Guidelines</h2>
                        <p>Please follow these guidelines to ensure that your customized golf balls meet your expectations.</p>
                        <ul>
                            <li>For both images and text, drag and drop the red dots on the corners of your content to expand.</li>
                            <li>Expand content to use as much of the space available in the red-dashed content area.</li>
                        </ul>
                        <div class="sample-images">
                            <div class="sample-image">
                                <img src="https://cdn.shopify.com/s/files/1/0554/9958/7689/files/franks_image_small_qstomizer.jpg?v=1784258485" alt="A golf ball where the logo is left small and does not fill the dashed content area." />
                            </div>
                            <div class="sample-image">
                                <img src="https://cdn.shopify.com/s/files/1/0554/9958/7689/files/franks_image_large_qstomizer.jpg?v=1784258485" alt="A golf ball where the logo has been expanded to fill the dashed content area." />
                            </div>
                        </div>
                        <ul>
                            <li>Position your expanded content as close to the intersection of the horizontal & vertical axes as possible.</li>
                        </ul>
                        <div class="sample-images">
                            <div class="sample-image">
                                <img src="https://cdn.shopify.com/s/files/1/0554/9958/7689/files/franks_text_centered_qstomizer.jpg?v=1784482590" alt="A golf ball with text positioned at the center where the horizontal and vertical guide lines cross." />
                            </div>
                            <div class="sample-image">
                                <img src="https://cdn.shopify.com/s/files/1/0554/9958/7689/files/franks_image_text_centered_qstomizer.jpg?v=1784482591" alt="A golf ball with both a logo and text centered on the point where the guide lines cross." />
                            </div>
                        </div>
                        <ul>
                            <li>Whenever possible, use images with a transparent background. The first of these sample balls will print with the logo on top of a white background.</li>
                        </ul>
                        <div class="sample-images">
                            <div class="sample-image">
                                <img src="https://cdn.shopify.com/s/files/1/0554/9958/7689/files/franks_logo_white_bg_qstomizer_2.jpg?v=1784485448" alt="A golf ball logo shown on a solid white rectangle, which will print as a visible white background on the ball." />
                            </div>
                            <div class="sample-image">
                                <img src="https://cdn.shopify.com/s/files/1/0554/9958/7689/files/franks_image_large_qstomizer.jpg?v=1784258485" alt="A golf ball with a transparent-background logo that sits cleanly on the ball with no white box." />
                            </div>
                        </div>
                        <button type="button" class="qstomizer-instructions--closer-inline modal-closer">
                            <span>Close and Customize!</span>
                        </button>
                    </div>
                    <button type="button" class="qstomizer-instructions--closer modal-closer" aria-label="Close guidelines">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                            <path d="M 38.982422 6.9707031 A 2.0002 2.0002 0 0 0 37.585938 7.5859375 L 24 21.171875 L 10.414062 7.5859375 A 2.0002 2.0002 0 0 0 8.9785156 6.9804688 A 2.0002 2.0002 0 0 0 7.5859375 10.414062 L 21.171875 24 L 7.5859375 37.585938 A 2.0002 2.0002 0 1 0 10.414062 40.414062 L 24 26.828125 L 37.585938 40.414062 A 2.0002 2.0002 0 1 0 40.414062 37.585938 L 26.828125 24 L 40.414062 10.414062 A 2.0002 2.0002 0 0 0 38.982422 6.9707031 z"/>
                        </svg>
                    </button>
                </div>
            `;
            document.body.append(this.modalEl);
        }

        // ---- State helpers -------------------------------------------------
        isOpen() {
            return this.modalEl.style.display !== 'none';
        }

        // Focusable elements currently inside the modal, in DOM order and visible.
        getFocusable() {
            const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
            return Array.from(this.modalEl.querySelectorAll(selector))
                .filter(el => !el.disabled && el.getClientRects().length > 0);
        }

        // Make everything behind the modal inert (blocks focus, pointer, and AT) so the
        // dialog is truly modal. Skips the modal itself and the trigger.
        setBackgroundInert(on) {
            Array.from(document.body.children).forEach(child => {
                if (child === this.modalEl || child === this.triggerEl) return;
                if (on) child.setAttribute('inert', '');
                else child.removeAttribute('inert');
            });
        }

        // ---- Open / close --------------------------------------------------
        open() {
            this.lastFocused = document.activeElement;
            this.modalEl.style.display = 'block';
            this.triggerEl.classList.add('qstomizer-instructions-open');
            this.triggerEl.setAttribute('aria-expanded', 'true');
            document.body.classList.add('qstomizer-instructions-open');
            this.setBackgroundInert(true);
            this.modalEl.focus(); // land on the dialog so screen readers announce the title
        }

        close() {
            this.modalEl.style.display = 'none';
            this.triggerEl.classList.remove('qstomizer-instructions-open');
            this.triggerEl.setAttribute('aria-expanded', 'false');
            document.body.classList.remove('qstomizer-instructions-open');
            this.setBackgroundInert(false);
            // Return focus to whatever opened it; fall back to the trigger (e.g. auto-open on load).
            const target = (this.lastFocused && this.lastFocused !== document.body && document.contains(this.lastFocused))
                ? this.lastFocused
                : this.triggerEl;
            target.focus();
        }

        // ---- Events --------------------------------------------------------
        onClick(e) {
            const opener = e.target.closest('.qstomizer-instructions--trigger');
            const closer = e.target.closest('.qstomizer-instructions--modal-inner .modal-closer');
            const outer = e.target.closest('.qstomizer-instructions--modal-outer');
            const inner = e.target.closest('.qstomizer-instructions--modal-inner');

            if (closer || (outer && !inner)) {
                this.close();
            } else if (opener) {
                this.open();
            }
        }

        // Escape to close + Tab focus trap, only while open.
        onKeydown(e) {
            if (!this.isOpen()) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                this.close();
                return;
            }

            if (e.key === 'Tab') {
                const focusable = this.getFocusable();
                if (!focusable.length) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                const active = document.activeElement;

                if (e.shiftKey && (active === first || active === this.modalEl)) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && active === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    });
}
